import EditMode from './editMode';
import { formatDate } from 'src/utils/formatDate';
import { formatDate as formatTime } from 'src/utils/helpers';
import ImageLogo from '../../../assets/images/de-full-logo.png';
import InteractionLayer from 'src/components/ui/interactionLayer';
import ReactDOM from 'react-dom';
import { useLocalization } from 'src/utils/useLocalization';
import { useReactToPrint } from 'react-to-print';
import { useEffect, useRef, useState } from 'react';
import './details.scss';

interface DetailsViewProps {
    data?: any;
    detailsTitle?: any;
    isDetailsLoading?: boolean;
    isEditApiError?: boolean;
    open?: boolean;
    updateOpenLayer: (arg: boolean) => void;
    updateEditedValues: (arg?: any) => void;
}

const DetailsView = ({
    data,
    detailsTitle,
    isDetailsLoading,
    isEditApiError,
    open,
    updateOpenLayer,
    updateEditedValues,
}: DetailsViewProps) => {
    const {
        commonLabels: { EMPTY_FIELD },
        errorMessages: { API_FAILURE },
        labelText,
        detailsView,
    } = useLocalization();

    const componentRef = useRef(null);

    const [editableFieldValues, setEditableFieldValues] = useState<
        Map<string, any>
    >(new Map());

    const normalizeApiDateToYMD = (value?: string | null): string | null => {
        if (!value || typeof value !== 'string') return null;

        const trimmedValue = value.trim();
        if (!trimmedValue) return null;

        const ymdMatch = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!ymdMatch) return trimmedValue;

        return `${ymdMatch[1]}-${ymdMatch[2]}-${ymdMatch[3]}`;
    };

    const formatEditableDateFromYMD = (value?: string | null): string => {
        if (!value || typeof value !== 'string') return '';

        const normalizedValue = normalizeApiDateToYMD(value);
        if (!normalizedValue) return '';

        const [year, month, day] = normalizedValue.split('-');
        if (!year || !month || !day) return normalizedValue;

        return `${month}/${day}/${year}`;
    };

    useEffect(() => {
        if (data && Object.keys(data).length > Number('0')) {
            const editableFields = [
                'StockNumber',
                'Mileage',
                'MileageDate',
                'Notes',
            ];

            const newMap = new Map<string, any>();

            editableFields.forEach((item) => {
                const val =
                    data[item] && item === 'MileageDate'
                        ? normalizeApiDateToYMD(data[item])
                        : data[item];

                newMap.set(item, val || null);
            });

            setEditableFieldValues(newMap);
        }
    }, [data]);

    const updateEditFieldValue = (arg?: any): void => {
        const { value, replacingParentId } = arg || {};

        const editEleParent = document.getElementById(replacingParentId);
        if (!editEleParent) return;

        const elementWrapper = editEleParent.querySelector<HTMLElement>(
            `#${arg?.replacingElementId}`,
        );
        if (elementWrapper) {
            elementWrapper.style.display = 'block';
        }

        const editEle = editEleParent.querySelector<HTMLElement>('#edit-view');
        if (editEle) {
            editEle.style.display = 'none';
        }

        if (arg?.action === 'save') {
            const updatedMap = new Map(editableFieldValues);
            updatedMap.set(replacingParentId, value);
            setEditableFieldValues(updatedMap);
            updateEditedValues(Object.fromEntries(updatedMap));
        }
    };

    const handleEdit = (element: any, fieldVal?: any) => {
        const currentDiv = element.target.parentNode.id;
        const parentId = element.target.parentNode.parentNode.id;
        const parentWrapper = document.getElementById(parentId);

        if (!parentWrapper) return;

        let inputFieldType = 'text';
        let isNumeric = false;
        let isAlphanumeric = false;

        switch (parentId) {
            case 'StockNumber':
                inputFieldType = 'text';
                isAlphanumeric = true;
                break;
            case 'Mileage':
                inputFieldType = 'text';
                isNumeric = true;
                break;
            case 'Notes':
                inputFieldType = 'textArea';
                break;
            case 'MileageDate':
                inputFieldType = 'datePicker';
                break;
            default:
                inputFieldType = 'text';
                break;
        }

        const currentWrapper = parentWrapper.querySelector<HTMLElement>(
            `#${currentDiv}`,
        );
        const editableFieldWrapper =
            parentWrapper.querySelector<HTMLElement>('#edit-view');

        if (!editableFieldWrapper || !currentWrapper) return;

        editableFieldWrapper.style.display = 'flex';

        ReactDOM.render(
            <EditMode
                onLoadValue={fieldVal}
                inputFieldType={inputFieldType}
                isNumeric={isNumeric}
                isAlphanumeric={isAlphanumeric}
                replacingElementId={currentDiv}
                replacingParentId={parentId}
                updateEditFieldValue={updateEditFieldValue}
            />,
            editableFieldWrapper,
        );

        currentWrapper.style.display = 'none';
    };

    const displayEditableField = (field?: any, fieldType?: string) => {
        const rawValue =
            (fieldType && editableFieldValues.has(fieldType)
                ? editableFieldValues.get(fieldType)
                : field) ?? '';

        let fieldVal =
            rawValue === '' || rawValue === null || rawValue === undefined
                ? EMPTY_FIELD
                : rawValue;

        if (fieldVal !== EMPTY_FIELD) {
            fieldVal =
                fieldType === 'MileageDate'
                    ? formatEditableDateFromYMD(fieldVal)
                    : fieldVal;
        }

        return (
            <span id={fieldType}>
                <span id='default-view'>
                    <span className='editable-field'>{fieldVal}</span>
                    <i
                        className='c-icon c-icon--xxsmall c-icon--[semantic-edit] action-icons'
                        role='img'
                        aria-hidden='true'
                        onClick={(ele) => handleEdit(ele, rawValue)}
                    ></i>
                </span>
                <div id='edit-view'></div>
            </span>
        );
    };

    const displayStatus = (statusColor?: any) => {
        const colorStatus = statusColor ? statusColor?.toLowerCase() : null;
        const statusMap: Record<string, string> = {};
        statusMap['green'] = detailsView.ACTIVE;
        statusMap['yellow'] = detailsView.MATURITY;
        statusMap['orange'] = detailsView.BEYOND_TERM;
        statusMap['red'] = detailsView.FORCE_GROUND;

        const statusText =
            colorStatus && statusMap[colorStatus]
                ? statusMap[colorStatus]
                : null;

        return (
            <span>
                <span className={`${colorStatus} statusIndicator`} />
                <span>{statusText}</span>
            </span>
        );
    };

    const createList = (options?: any) => {
        const list = options?.map((option: string, key: number) => {
            return (
                <li className='options-list-items' key={key}>
                    {option}
                </li>
            );
        });
        return list;
    };

    const formatAmount = (value?: any) => {
        if (!isNaN(value)) {
            const formatting_options = {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
            };
            const dollarString = new Intl.NumberFormat(
                'en-US',
                formatting_options,
            );
            return dollarString.format(value);
        }
    };

    const detailsContent = (data?: any) => {
        return (
            <div>
                <div className='car'>
                    <h4 className='section-header'>{data.Description}</h4>
                    <div className='details-row'>
                        <span className='column labels'>{detailsView.VIN}</span>
                        <span className='column'>{data.Vin}</span>
                    </div>
                    <div className='details-row'>
                        <span className='column labels'>
                            {detailsView.MAKE}
                        </span>
                        <span className='column'>{data.Make}</span>
                    </div>
                    <div className='details-row'>
                        <span className='column labels'>
                            {detailsView.MODELCODE}
                        </span>
                        <span className='column'>{data.ModelCode}</span>
                    </div>
                    <div className='details-row'>
                        <span className='column labels'>
                            {detailsView.COLOR}
                        </span>
                        <span className='column'>{data.Color}</span>
                    </div>
                    <div className='details-row'>
                        <span className='column labels'>
                            {detailsView.OPTIONS}
                        </span>
                        <span className='column'>
                            <ul className='options-list'>
                                {createList(data.Options)}
                            </ul>
                        </span>
                    </div>
                    <div className='details-row'>
                        <span className='column labels'>
                            {detailsView.STOCKNO}
                        </span>
                        <span className='column' id='editablePlaceholder'>
                            {displayEditableField(
                                data.StockNumber,
                                'StockNumber',
                            )}
                        </span>
                    </div>
                    <div className='details-row'>
                        <span className='column labels'>
                            {detailsView.MILEAGE}
                        </span>
                        <span className='column'>
                            {displayEditableField(data.Mileage, 'Mileage')}
                        </span>
                    </div>
                    <div className='details-row'>
                        <span className='column labels'>
                            {detailsView.MILEAGE_DATE}
                        </span>
                        <span className='column'>
                            {displayEditableField(
                                data.MileageDate,
                                'MileageDate',
                            )}
                        </span>
                    </div>
                    <div className='details-row'>
                        <span className='column labels'>
                            {detailsView.NOTES}
                        </span>
                        <span className='column'>
                            {displayEditableField(data.Notes, 'Notes')}
                        </span>
                    </div>
                </div>
                <div className='account'>
                    <h4 className='section-header'>
                        {detailsView.ACCOUNT_INFO}
                    </h4>
                    <div className='details-row'>
                        <span className='column labels'>
                            {detailsView.LEASE_CONTRACT_ID}
                        </span>
                        <span className='column'>{data.LeaseContractId}</span>
                    </div>
                    <div className='details-row'>
                        <span className='column labels'>
                            {detailsView.IN_SERVICE_DATE}
                        </span>
                        <span className='column'>
                            {data.InServiceDate &&
                                formatDate(data.InServiceDate)}
                        </span>
                    </div>
                    <div className='details-row'>
                        <span className='column labels'>
                            {detailsView.LEASE_TERM}
                        </span>
                        <span className='column'>
                            {data.LeaseTerm} {detailsView.MONTHS}
                        </span>
                    </div>
                    <div className='details-row'>
                        <span className='column labels'>
                            {detailsView.MATURITY_DATE}
                        </span>
                        <span className='column'>
                            {data.MaturityDate && formatDate(data.MaturityDate)}
                        </span>
                    </div>
                    <div className='details-row'>
                        <span className='column labels'>
                            {detailsView.SPECIAL_PGM_CODE}
                        </span>
                        <span className='column'>
                            {data.SpecialProgramCode}
                        </span>
                    </div>
                    <div className='details-row'>
                        <span className='column labels'>
                            {detailsView.LENGTH_OF_SERVICE}
                        </span>
                        <span className='column'>
                            {data.LengthOfService} {detailsView.MONTHS}
                        </span>
                    </div>
                    <div className='details-row'>
                        <span className='column labels'>
                            {detailsView.REMAINING_PAYMENTS}
                        </span>
                        <span className='column'>{data.RemainingPayments}</span>
                    </div>
                    <div className='details-row'>
                        <span className='column labels'>
                            {detailsView.STATUS}
                        </span>
                        <span className='column'>
                            {displayStatus(data.LeaseStatus)}
                        </span>
                    </div>
                    <div className='details-row'>
                        <span className='column labels'>
                            {detailsView.MSRP}
                        </span>
                        <span className='column'>
                            {formatAmount(data.Msrp)}
                        </span>
                    </div>
                    <div className='details-row'>
                        <span className='column labels'>
                            {detailsView.MONTHLY_PAYMENT}
                        </span>
                        <span className='column'>
                            {formatAmount(data.MonthlyLeasePayment)}
                        </span>
                    </div>
                    <div className='details-row'>
                        <span className='column labels'>
                            {detailsView.CURRENTPURCHAERPRICE}
                        </span>
                        <span className='column'>
                            {formatAmount(data.CurrentPurchasePrice)}
                        </span>
                    </div>
                    <div className='details-row'>
                        <span className='column labels'>
                            {detailsView.REMAINING_PAYMENTS_ON_LEASE}
                        </span>
                        <span className='column'>
                            {formatAmount(data.RemainingPaymentsOnLease)}
                        </span>
                    </div>
                    <div className='details-row'>
                        <span className='column labels'>
                            {detailsView.BUYOUT_AT_TERM}
                        </span>
                        <span className='column'>
                            {formatAmount(data.BuyoutAtTerm)}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    const footerElement = () => {
        return (
            <div className='details-view-footer'>
                <button
                    className='c-btn c-btn--link c-btn--small c-btn--simple'
                    onClick={handlePrint}
                >
                    <i
                        className='c-btn__icon c-icon c-icon--[semantic-print]'
                        aria-hidden='true'
                        role='img'
                    ></i>
                    <span className='c-btn__text'>
                        {labelText.LABEL_TEXT_PRINT}
                    </span>
                </button>
            </div>
        );
    };

    const apiErrorComponent = () => {
        return (
            <div className='api-error'>
                <div className='api-error-message'>{API_FAILURE}</div>
            </div>
        );
    };

    const handlePrint = useReactToPrint({
        content: () => {
            if (componentRef.current) {
                const tableStat = (componentRef.current as Element).cloneNode(
                    true,
                );
                const PrintElem = document.createElement('div');
                const header = `<div style='display: flex; flex-direction:row; align-items:center; justify-content:space-between; width:100%; padding-top:28px; padding-left:28px; padding-right:28px;'> <img src='${ImageLogo}' height='19px' width='170px' /> <p style='margin-bottom:0px;'>${formatTime(
                    '',
                )}</p></div><h5 style='margin:28px;'>${detailsTitle}</h5>`;
                PrintElem.innerHTML = header;
                PrintElem.appendChild(tableStat);
                return PrintElem;
            }
            return null;
        },
    });

    return (
        <>
            <InteractionLayer
                apiErrorContent={isEditApiError && apiErrorComponent()}
                ref={componentRef}
                bodyContent={
                    <div className='details-view'>{detailsContent(data)}</div>
                }
                open={open}
                titleContent={detailsTitle}
                footerContent={footerElement()}
                loading={isDetailsLoading}
                updateOpenLayer={updateOpenLayer}
            />
        </>
    );
};

export default DetailsView;
